import { message } from 'antd';
import { put, takeLatest } from 'redux-saga/effects';
import {
  BasicDetailsData,
  ProductBucketPayload,
  setError,
  setField,
  setLoading,
  setSuccess,
  submitData,
  submitOwnerDetails,
  submitProductBucket,
} from 'redux/slice/agency-onbording/BasicDetails.slice';
import { OwnerDetailsPayload } from 'redux/types/agency-onboarding/Agency.types';
import { agencyOnboarding } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

// Define the payload structure
interface BasicDetailsPayload {
  agency_id: string;
  company_type: string;
  year_of_business: string;
  channel_list: {
    channelName: string;
    totalAgents: number;
    totalAvailableAgents: number;
    languageList: string[];
  }[];
  allocator_list: {
    allocatorId: string;
    status: string;
    totalAgents: number;
  }[];
  product_bucket_list: {
    product_name: string;
    bucket_name: string;
  }[];
}

function* handleBasicDetailsSubmission(action: {
  type: string;
  payload: BasicDetailsData;
}): any {
  try {
    const obj: any = action.payload;
    const collectionChannels: any[] = [];

    // Build collectionChannels array
    obj.collectionChannels?.forEach((channel: string) => {
      if (channel === 'field') {
        collectionChannels.push({
          channelName: channel,
          totalAgents: obj.totalFosAgents,
          totalAvailableAgents: obj.totalFosAgents,
          languageList: obj.languagesCateredOnCalling,
        });
      } else {
        collectionChannels.push({
          channelName: channel,
          totalAgents: obj.totalTeleCallingAgents,
          totalAvailableAgents: obj.totalTeleCallingAgents,
          languageList: obj.languagesCateredOnCalling,
        });
      }
    });

    if (collectionChannels.length > 0) {
      obj.collectionChannels = collectionChannels;
    }
    delete obj.totalNumberOfAgents;
    delete obj.languagesCateredOnCalling;

    yield put(setLoading(true));
    const response = yield HTTPClient.PostRequest(
      agencyOnboarding.AGENCY_BASIC_DETAILS,
      obj
    );
    if (response.status === 200) {
      if (response.data.errors && response.data.errors.length > 0) {
        message.error(response.data.errors[0].message);
        yield put(setError(response.data.errors[0].message));
        return;
      }
      message.success('Basic details updated successfully');
      const existingData = JSON.parse(
        localStorage.getItem('agencyData') || '{}'
      );
      const mergedData = { ...existingData, ...response.data.data };
      if (
        !response.data.data.productBucketList &&
        existingData.productBucketList
      ) {
        mergedData.productBucketList = existingData.productBucketList;
      }
      localStorage.setItem('agencyData', JSON.stringify(mergedData));
      yield put(setField(mergedData));
    }
  } catch (error: any) {
    message.error(
      error.response?.data?.message || 'Failed to submit basic details.'
    );
    yield put(
      setError(
        error.response?.data?.message || 'Failed to submit basic details.'
      )
    );
  } finally {
    yield put(setLoading(false)); // Stop loading
  }
}

// Handle product bucket submission
function* handleProductBucketSubmission(action: {
  type: string;
  payload: ProductBucketPayload;
}): any {
  try {
    const payload = action.payload;

    yield put(setLoading(true));
    const response = yield HTTPClient.PostRequest(
      agencyOnboarding.AGENCY_PRODUCT_BUCKET,
      payload
    );

    if (response.status === 200 || response.status === 201) {
      message.success('Product bucket updated successfully');

      // Merge V2 response into existing agencyData in localStorage
      const storedData = localStorage.getItem('agencyData');
      const responseData = response.data.data;

      // Normalize: API returns productBucketConfig with isExpert
      const productBucketList = (
        responseData.productBucketConfig ||
        responseData.productBucketList ||
        []
      ).map((item: any) => ({
        productId: item.productId,
        bucketIds: item.bucketIds,
        allocatorIds: item.allocatorIds,
        is_expert: item.isExpert ?? item.is_expert ?? false,
      }));

      if (storedData) {
        const existingAgencyData = JSON.parse(storedData);
        const mergedData = {
          ...existingAgencyData,
          productBucketList,
        };
        localStorage.setItem('agencyData', JSON.stringify(mergedData));
        yield put(setField(mergedData));
      } else {
        yield put(setField({ ...responseData, productBucketList }));
      }
      yield put(setSuccess(true));
    }
  } catch (error: any) {
    yield put(
      setError(
        error.response?.data?.message ||
          'Failed to submit product bucket details.'
      )
    );
  } finally {
    yield put(setLoading(false)); // Stop loading
  }
}

// Handle owner details submission
function* handleOwnerDetailsSubmission(action: {
  type: string;
  payload: OwnerDetailsPayload;
}): any {
  try {
    const payload = action.payload;

    yield put(setLoading(true));
    const response = yield HTTPClient.PostRequest(
      agencyOnboarding.AGENCY_OWNER_DETAILS,
      payload
    );

    if (response.status === 200 || response.status === 201) {
      const existingData = JSON.parse(
        localStorage.getItem('agencyData') || '{}'
      );
      message.success('Owner details updated successfully');
      const mergedData = { ...existingData, ...response.data.data };
      if (
        !response.data.data.productBucketList &&
        existingData.productBucketList
      ) {
        mergedData.productBucketList = existingData.productBucketList;
      }
      localStorage.setItem('agencyData', JSON.stringify(mergedData));

      yield put(setField(mergedData));
      yield put(setSuccess(true));
    }
  } catch (error: any) {
    yield put(
      setError(
        error.response?.data?.message || 'Failed to submit owner details.'
      )
    );
  } finally {
    yield put(setLoading(false)); // Stop loading
  }
}

// Watcher saga
function* watchBasicDetailsSubmission() {
  yield takeLatest(submitData.type, handleBasicDetailsSubmission);
  yield takeLatest(submitProductBucket.type, handleProductBucketSubmission);
  yield takeLatest(submitOwnerDetails.type, handleOwnerDetailsSubmission);
}

export default watchBasicDetailsSubmission;
